import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { usersApi, smsApi } from '../../services/api';
import {
    MessageSquare,
    Search,
    Mail,
    Phone,
    Shield,
    AlertCircle,
    CheckCircle,
    Save,
    Users,
    ChevronRight,
    ChevronLeft,
} from 'lucide-react';

/* ─── Reusable user-card row ─────────────────────────────────────────────── */
const UserRow = ({ user, selected, onToggle, getRoleName, getRoleBadgeColor }) => (
    <div
        onClick={() => onToggle(user.id)}
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b last:border-b-0 transition-colors select-none ${selected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
            }`}
    >
        <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(user.id)}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
        />
        <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 ${selected
                ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                : 'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}
        >
            {user.fullName?.charAt(0).toUpperCase() || 'U'}
        </div>
        <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate">{user.fullName}</p>
            <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
                <Mail className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{user.email}</span>
            </div>
            {user.phoneNumber && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Phone className="w-3 h-3 flex-shrink-0" />
                    <span>{user.phoneNumber}</span>
                </div>
            )}
        </div>
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${getRoleBadgeColor(
                user.roleId
            )}`}
        >
            <Shield className="w-3 h-3" />
            {getRoleName(user.roleId)}
        </span>
    </div>
);

/* ─── Panel component ────────────────────────────────────────────────────── */
const Panel = ({ title, icon: Icon, accentClass, users, selectedIds, onToggle, search, onSearch, count, emptyText }) => {
    const getRoleName = (role) => {
        const roles = { 0: 'Süper Admin', 1: 'Yönetici', 2: 'Personel' };
        return roles[role] || 'Bilinmiyor';
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            0: 'bg-red-100 text-red-700',
            1: 'bg-blue-100 text-blue-700',
            2: 'bg-gray-100 text-gray-700',
        };
        return colors[role] || 'bg-gray-100 text-gray-700';
    };

    const allSelected = users.length > 0 && users.every((u) => selectedIds.includes(u.id));
    const selectedCount = selectedIds.filter((id) => users.some((u) => u.id === id)).length;

    const handleToggleAll = () => {
        users.forEach((u) => {
            const isSelected = selectedIds.includes(u.id);
            if (allSelected && isSelected) onToggle(u.id, false);
            else if (!allSelected && !isSelected) onToggle(u.id, true);
        });
    };

    return (
        <div className="flex-1 bg-white rounded-xl shadow-sm border flex flex-col min-h-0 overflow-hidden">
            {/* Panel Header */}
            <div className={`px-4 py-3 border-b ${accentClass}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Icon className="w-5 h-5" />
                        <span className="font-semibold text-sm">{title}</span>
                    </div>
                    <span className="text-sm font-bold">{count}</span>
                </div>
            </div>

            {/* Search */}
            <div className="px-4 py-2 border-b bg-gray-50">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => onSearch(e.target.value)}
                        placeholder="Ara..."
                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>

            {/* Select-all bar */}
            {users.length > 0 && (
                <div
                    onClick={handleToggleAll}
                    className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleToggleAll}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-500 font-medium">
                        Tümünü seç ({users.length})
                    </span>
                    {selectedCount > 0 && (
                        <span className="ml-auto text-xs text-blue-600 font-semibold">
                            {selectedCount} seçili
                        </span>
                    )}
                </div>
            )}

            {/* User list */}
            <div className="flex-1 overflow-y-auto">
                {users.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full py-12 text-gray-400">
                        <Users className="w-10 h-10 mb-2 text-gray-300" />
                        <p className="text-sm">{emptyText}</p>
                    </div>
                ) : (
                    users.map((user) => (
                        <UserRow
                            key={user.id}
                            user={user}
                            selected={selectedIds.includes(user.id)}
                            onToggle={onToggle}
                            getRoleName={getRoleName}
                            getRoleBadgeColor={getRoleBadgeColor}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

/* ─── Main page ──────────────────────────────────────────────────────────── */
const SmsSettingsPage = () => {
    const { selectedCorporate } = useOutletContext();

    const [allUsers, setAllUsers] = useState([]);
    const [smsUserIds, setSmsUserIds] = useState([]);

    const [leftSelected, setLeftSelected] = useState([]);
    const [rightSelected, setRightSelected] = useState([]);

    const [leftSearch, setLeftSearch] = useState('');
    const [rightSearch, setRightSearch] = useState('');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        if (selectedCorporate) loadData();
    }, [selectedCorporate]);

    const loadData = async () => {
        if (!selectedCorporate?.id) return;
        setLoading(true);
        setError(null);
        try {
            const [usersData, smsData] = await Promise.all([
                usersApi.getAll(selectedCorporate.id),
                smsApi.getSmsUsers(selectedCorporate.id),
            ]);
            setAllUsers(usersData.filter((u) => u.status === 1));
            setSmsUserIds(smsData.userIds || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /* derived lists */
    const leftUsers = allUsers
        .filter((u) => !smsUserIds.includes(u.id))
        .filter(
            (u) =>
                u.fullName?.toLowerCase().includes(leftSearch.toLowerCase()) ||
                u.email?.toLowerCase().includes(leftSearch.toLowerCase()) ||
                u.phoneNumber?.includes(leftSearch)
        );

    const rightUsers = allUsers
        .filter((u) => smsUserIds.includes(u.id))
        .filter(
            (u) =>
                u.fullName?.toLowerCase().includes(rightSearch.toLowerCase()) ||
                u.email?.toLowerCase().includes(rightSearch.toLowerCase()) ||
                u.phoneNumber?.includes(rightSearch)
        );

    /* toggle helpers — optional bulkAdd flag avoids double-flipping */
    const toggleLeft = (id, bulkAdd) => {
        if (bulkAdd !== undefined) {
            setLeftSelected((prev) =>
                bulkAdd ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
            );
        } else {
            setLeftSelected((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
            );
        }
    };

    const toggleRight = (id, bulkAdd) => {
        if (bulkAdd !== undefined) {
            setRightSelected((prev) =>
                bulkAdd ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
            );
        } else {
            setRightSelected((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
            );
        }
    };

    /* move actions */
    const moveSelectedToRight = () => {
        if (leftSelected.length === 0) return;
        setSmsUserIds((prev) => [...new Set([...prev, ...leftSelected])]);
        setLeftSelected([]);
    };

    const moveSelectedToLeft = () => {
        if (rightSelected.length === 0) return;
        setSmsUserIds((prev) => prev.filter((id) => !rightSelected.includes(id)));
        setRightSelected([]);
    };

    /* save */
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            await smsApi.updateSmsUsers(selectedCorporate.id, smsUserIds);
            setSuccessMessage('SMS kullanıcıları başarıyla kaydedildi.');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full space-y-4">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">SMS Ayarları</h1>
                    <p className="text-gray-500 mt-1">
                        SMS bildirimi alacak kullanıcıları sağ panele taşıyın
                    </p>
                </div>
            </div>

            {/* Alerts */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <p className="text-red-700 text-sm">{error}</p>
                </div>
            )}
            {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <p className="text-green-700 text-sm">{successMessage}</p>
                </div>
            )}

            {/* Transfer List */}
            <div className="flex gap-3 items-stretch" style={{ minHeight: '520px' }}>

                {/* LEFT — all users (not in SMS list) */}
                <Panel
                    title="Tüm Kullanıcılar"
                    icon={Users}
                    accentClass="bg-gray-50 text-gray-700"
                    users={leftUsers}
                    selectedIds={leftSelected}
                    onToggle={toggleLeft}
                    search={leftSearch}
                    onSearch={setLeftSearch}
                    count={allUsers.filter((u) => !smsUserIds.includes(u.id)).length}
                    emptyText="Eklenecek kullanıcı yok"
                />

                {/* CENTER — action buttons */}
                <div className="flex flex-col items-center justify-center gap-2 py-4 flex-shrink-0">
                    <button
                        onClick={moveSelectedToRight}
                        disabled={leftSelected.length === 0}
                        title="Seçilileri ekle"
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-blue-50 hover:border-blue-400 text-gray-500 hover:text-blue-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                        onClick={moveSelectedToLeft}
                        disabled={rightSelected.length === 0}
                        title="Seçilileri çıkar"
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-red-50 hover:border-red-400 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                </div>

                {/* RIGHT — SMS users */}
                <Panel
                    title="SMS Alacak Kullanıcılar"
                    icon={MessageSquare}
                    accentClass="bg-green-50 text-green-700"
                    users={rightUsers}
                    selectedIds={rightSelected}
                    onToggle={toggleRight}
                    search={rightSearch}
                    onSearch={setRightSearch}
                    count={smsUserIds.length}
                    emptyText="Henüz SMS kullanıcısı eklenmedi"
                />
            </div>

            {/* Bottom Save */}
            <div className="flex justify-end pb-2">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
                >
                    <Save className="w-5 h-5" />
                    {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                </button>
            </div>
        </div>
    );
};

export default SmsSettingsPage;
